import type { GameEndViewModel } from "@/blocks/game-end/types";

type GameEndModalProps = {
  outcome: GameEndViewModel;
  onReturn: () => void;
  onCopyPgn: () => void;
  onRematch: () => void;
  onDismiss?: () => void;
  copyState: "idle" | "copying" | "copied" | "error";
  rematchState: "idle" | "starting" | "error";
  copyError?: string | null;
  rematchError?: string | null;
};

export function GameEndModal({
  outcome,
  onReturn,
  onCopyPgn,
  onRematch,
  onDismiss,
  copyState,
  rematchState,
  copyError = null,
  rematchError = null,
}: GameEndModalProps) {
  const copyButtonLabel =
    copyState === "copied"
      ? "Copied"
      : copyState === "copying"
        ? "Copying"
        : "Copy";
  const rematchButtonLabel = rematchState === "starting" ? "Starting rematch" : "Rematch";

  return (
    <div className="game-end-backdrop" role="presentation">
      <section
        className={`game-end-modal game-end-modal--${outcome.tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="game-end-title"
      >
        {onDismiss ? (
          <button
            className="game-end-modal__dismiss"
            type="button"
            aria-label="Close result and review game"
            onClick={onDismiss}
          >
            x
          </button>
        ) : null}

        <div className="game-end-modal__crest-panel">
          {outcome.eyebrow ? (
            <p className="game-end-modal__eyebrow">{outcome.eyebrow}</p>
          ) : null}
          <div className="game-end-modal__crest" aria-hidden="true">
            <span>{outcome.crest}</span>
          </div>
        </div>

        <div className="game-end-modal__content">
          <h2 id="game-end-title">{outcome.title}</h2>
          <div className="game-end-modal__result-row">
            <p className="game-end-modal__subtitle">{outcome.subtitle}</p>
            <button
              className={`game-end-modal__copy-chip game-end-modal__copy-chip--${copyState}`}
              type="button"
              onClick={onCopyPgn}
              disabled={copyState === "copying"}
              aria-label={copyState === "copied" ? "PGN copied" : "Copy PGN"}
            >
              <span className="game-end-modal__copy-mark" aria-hidden="true">
                {copyState === "copied" ? "✓" : copyState === "copying" ? "…" : "PGN"}
              </span>
              <span>{copyButtonLabel}</span>
            </button>
          </div>

          <div className="game-end-modal__actions">
            <button
              className="button-link button-link--secondary game-end-modal__return-button"
              type="button"
              onClick={onReturn}
            >
              Return
            </button>
            <button
              className="button-link button-link--primary game-end-modal__rematch-button"
              type="button"
              onClick={onRematch}
              disabled={rematchState === "starting"}
            >
              {rematchButtonLabel}
            </button>
          </div>

          {copyState === "error" && copyError ? (
            <p className="game-end-modal__error">{copyError}</p>
          ) : null}
          {rematchState === "error" && rematchError ? (
            <p className="game-end-modal__error">{rematchError}</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
